import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  InputBase,
} from "@mui/material";
import {
  Add as AddIcon,
  Search,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PlantGrid from "./grid/PlantGrid";
import type { Plant } from "./plant.types";
import BackdropLoader from "../../components/common/BackdropLoader";
import {
  useLazyGetAllPlantsDetailsQuery,
  useDeletePlantMutation,
} from "../../api/services/plantApi";
import Swal from "sweetalert2";

const PlantManagement: React.FC = () => {
  const [triggerGetPlants, { isLoading }] = useLazyGetAllPlantsDetailsQuery();
  const [deletePlant, { isLoading: isDeleting }] = useDeletePlantMutation();
  const [state, setState] = useState({
    plants: [] as Plant[],
    searchTerm: "",
    page: 1,
    hasMore: true,
    isLoadingMore: false,
    isInitialLoad: true,
  });
  const navigate = useNavigate();

  // Transform plant data function
  const transformPlantData = (data: any[]): Plant[] => {
    return data.map((item: any) => ({
      id: item.id.toString(),
      name: item.name,
      description: item.description || item.Description,
      autoClosureTime: item.autoClosureTime || "00:00:00",
      shifts: item.shifts || [{ start: "00:00", end: "00:00" }],
      lines: item.lines || "01",
      timezone: item.timezone || "",
    }));
  };

  // Initial load - only runs once
  useEffect(() => {
    const loadInitialPlants = async () => {
      try {
        const result = await triggerGetPlants(
          {
            pageNum: 1,
            search: "",
          },
          true
        ).unwrap();

        const transformedPlants = transformPlantData(result.plants || []);

        setState((prev) => ({
          ...prev,
          plants: transformedPlants,
          hasMore: result.hasMore,
          page: 1,
          isInitialLoad: false,
        }));
      } catch (err) {
        console.error("Error loading plants:", err);
        setState((prev) => ({ ...prev, isInitialLoad: false }));
      }
    };

    loadInitialPlants();
  }, []); // Empty dependency - runs only once

  // Search effect with debouncing
  useEffect(() => {
    if (state.isInitialLoad) return; // Skip if initial load hasn't completed

    const timer = setTimeout(async () => {
      try {
        const result = await triggerGetPlants(
          {
            pageNum: 1,
            search: state.searchTerm,
          },
          true
        ).unwrap();

        const transformedPlants = transformPlantData(result.plants || []);

        setState((prev) => ({
          ...prev,
          plants: transformedPlants,
          hasMore: result.hasMore,
          page: 1,
        }));
      } catch (err) {
        console.error("Error searching plants:", err);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [state.searchTerm]); // Only depends on searchTerm

  // Scroll handler for pagination
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const handleScroll = async () => {
      if (isFetchingRef.current || !state.hasMore || state.isInitialLoad)
        return;

      const scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight || document.body.scrollHeight;
      const clientHeight =
        document.documentElement.clientHeight || window.innerHeight;

      if (scrollTop + clientHeight >= scrollHeight - 100) {
        isFetchingRef.current = true;

        try {
          const result = await triggerGetPlants(
            { pageNum: state.page + 1, search: state.searchTerm },
            true
          ).unwrap();

          const transformedPlants = transformPlantData(result.plants || []);

          setState((prev) => ({
            ...prev,
            plants: [...prev.plants, ...transformedPlants],
            hasMore: result.hasMore,
            page: prev.page + 1,
          }));
        } catch (err) {
          console.error("Error loading more plants:", err);
        } finally {
          isFetchingRef.current = false;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [state.hasMore, state.isInitialLoad, state.page, state.searchTerm]);

  const handleCreate = () => navigate("/plant/create");
  const handleEdit = (plant: Plant) => navigate(`/plant/edit/${plant.id}`);

  const handleDelete = async (plantId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click event

    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete this plant?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await deletePlant(plantId).unwrap();

          // Remove deleted plant from state
          setState((prev) => ({
            ...prev,
            plants: prev.plants.filter((plant) => plant.id !== plantId),
          }));

          Swal.fire({
            icon: "success",
            title: "Success!",
            text:
              res?.data?.message ||
              res?.data?.msg ||
              "Plant Deleted successfully",
            confirmButtonColor: "#003EB0",
            confirmButtonText: "OK",
          }).then(() => {
            navigate("/plant");
          });
        } catch (err: any) {
          console.error("Error deleting plant:", err);
          Swal.fire({
            icon: "error",
            title: "Error!",
            text:
              err?.data?.message ||
              err?.data?.msg ||
              "Failed to update plant. Please try again.",
            confirmButtonColor: "#003EB0",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        py: 2,
        p: { xs: 2, sm: 3 },
      }}
    >
      <BackdropLoader
        openStates={(isLoading && state.plants.length === 0) || isDeleting}
      />

      {/* Header Section */}
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        {/* Title */}
        <Grid size={6}>
          <Typography
            sx={{
              color: "#172B4D",
              fontSize: { xs: "18px", sm: "22px" },
              fontWeight: 600,
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            Plant Management
          </Typography>
        </Grid>

        {/* Search + Create Button */}
        <Grid size={6}>
          <Grid
            container
            spacing={1}
            justifyContent={{ xs: "center", sm: "flex-end" }}
            alignItems="center"
          >
            {/* Search */}
            <Grid>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 2,
                  px: 1,
                  py: 0.5,
                  bgcolor: "background.paper",
                }}
              >
                <Search sx={{ fontSize: 18, color: "grey.600", mr: 1 }} />
                <InputBase
                  placeholder="Search"
                  value={state.searchTerm}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      searchTerm: e.target.value,
                    }))
                  }
                  sx={{ fontSize: "0.875rem", width: 200 }}
                />
              </Box>
            </Grid>

            {/* Create Button */}
            <Grid>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
              >
                Create Plant
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Plant Grid */}
      <Box sx={{ mt: 3 }}>
        {state.plants.length > 0 ? (
          <>
            <PlantGrid
              plants={state.plants}
              onPlantClick={handleEdit}
              onPlantDelete={handleDelete}
            />
            {state.isLoadingMore && (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Loading more plants...
                </Typography>
              </Box>
            )}
            {!state.hasMore && state.plants.length > 0 && (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  No more plants
                </Typography>
              </Box>
            )}
          </>
        ) : !isLoading && !state.isInitialLoad ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography variant="h6" color="textSecondary">
              {state.searchTerm ? "No more Plants" : "No plants found"}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default PlantManagement;
